import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ConfigService } from '../core/config/config.service';
import { tokenEstaExpirado } from '../core/utils/token.util';
import { LoginRequest, LoginResponse, UsuarioResumo } from '../models/auth.model';
import { SessionStorageService } from './session-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly storage = inject(SessionStorageService);

  private readonly tokenSignal = signal<string | null>(this.storage.getToken());
  private readonly usuarioSignal = signal<UsuarioResumo | null>(this.storage.getUsuario());

  readonly usuario = this.usuarioSignal.asReadonly();
  /** Autenticado = há token e ele não está expirado. */
  readonly isAutenticado = computed(() => !tokenEstaExpirado(this.tokenSignal()));

  readonly papel = computed(() => this.usuarioSignal()?.papel ?? null);
  readonly isAdmin = computed(() => this.papel() === 'ADMIN');

  login(credenciais: LoginRequest): Observable<LoginResponse> {
    const url = `${this.config.settings()?.apiUrl}/auth/login`;
    return this.http.post<LoginResponse>(url, credenciais).pipe(
      tap((resposta) => {
        this.storage.setToken(resposta.token);
        this.storage.setUsuario(resposta.usuario);
        this.tokenSignal.set(resposta.token);
        this.usuarioSignal.set(resposta.usuario);
      }),
    );
  }

  /** Após trocar a senha, remove a exigência de troca da sessão. */
  marcarSenhaTrocada(): void {
    const atual = this.usuarioSignal();
    if (!atual) {
      return;
    }
    const atualizado: UsuarioResumo = { ...atual, trocaSenhaObrigatoria: false };
    this.storage.setUsuario(atualizado);
    this.usuarioSignal.set(atualizado);
  }

  logout(): void {
    this.storage.clear();
    this.tokenSignal.set(null);
    this.usuarioSignal.set(null);
  }

  getToken(): string | null {
    return this.tokenSignal();
  }
}
